import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";

interface FeatureSectionProps {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  variant?: "default" | "dark" | "highlight";
  className?: string;
}

export const FeatureSection = ({
  title,
  subtitle,
  children,
  variant = "default",
  className = "",
}: FeatureSectionProps) => {
  const bgClass = {
    default: "bg-[#0F172A]",
    dark: "bg-[#0A0A0F]",
    highlight: "bg-gradient-to-br from-[#1A365D]/20 to-[#0F172A]",
  }[variant];

  return (
    <section className={`relative py-20 ${bgClass} ${className}`}>
      <div className="container mx-auto px-4">
        {(title || subtitle) && (
          <div className="max-w-3xl mx-auto text-center mb-16">
            {title && (
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-lg text-white/50">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

// Composant pour afficher un problème/statistique
interface ProblemBlockProps {
  stat?: string;
  text: string;
  source?: string;
}

export const ProblemBlock = ({ stat, text, source }: ProblemBlockProps) => {
  return (
    <div className="relative max-w-3xl mx-auto">
      <div className="rounded-2xl p-[1px] bg-gradient-to-b from-[#EF4444]/30 to-transparent">
        <div className="rounded-2xl bg-[#EF4444]/5 backdrop-blur-xl p-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center">
              <span className="text-lg">⚠️</span>
            </div>
            <div>
              {stat && (
                <span className="text-4xl font-bold text-[#EF4444] block mb-2">
                  {stat}
                </span>
              )}
              <p className="text-lg text-white/80 leading-relaxed">
                "{text}"
              </p>
              {source && (
                <p className="text-sm text-white/40 mt-3">
                  — {source}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour les étapes "Comment ça marche"
interface StepProps {
  number: number;
  title: string;
  description: string;
  icon?: LucideIcon;
}

export const Step = ({ number, title, description, icon: Icon }: StepProps) => {
  return (
    <div className="relative">
      {/* Ligne de connexion */}
      <div className="absolute top-8 left-8 right-0 h-[2px] bg-gradient-to-r from-[#F59E0B]/50 to-transparent -z-10 hidden md:block" />

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 h-14 w-14 rounded-[12px] bg-[#131318] border border-white/[0.06] flex items-center justify-center">
          {Icon ? (
            <Icon className="h-6 w-6 text-white/50" strokeWidth={2.5} />
          ) : (
            <span className="text-xl font-bold text-white/50">{number}</span>
          )}
        </div>
        <div className="pt-2">
          <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
          <p className="text-white/50 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
};

// Composant pour les cartes de bénéfices
interface BenefitCardProps {
  icon?: LucideIcon | string;
  title: string;
  description: string;
  gradient?: string;
}

export const BenefitCard = ({
  icon,
  title,
  description,
  gradient = "from-[#F59E0B] to-[#D97706]"
}: BenefitCardProps) => {
  const isEmoji = typeof icon === "string";
  const Icon = !isEmoji ? icon : undefined;

  return (
    <div className="group rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent hover:from-[#F59E0B]/30 transition-all duration-300">
      <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6 h-full">
        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
          {isEmoji ? (
            <span className="text-xl">{icon}</span>
          ) : Icon ? (
            <Icon className="h-6 w-6 text-white" />
          ) : null}
        </div>
        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#F59E0B] transition-colors">
          {title}
        </h3>
        <p className="text-white/50 leading-relaxed text-sm">
          {description}
        </p>
      </div>
    </div>
  );
};

// Composant pour les alertes
interface AlertCardProps {
  level: "critical" | "warning" | "info";
  title: string;
  items: string[];
}

export const AlertCard = ({ level, title, items }: AlertCardProps) => {
  const config = {
    critical: {
      bg: "bg-[#EF4444]/10",
      border: "border-[#EF4444]/30",
      icon: "🔴",
      titleColor: "text-[#EF4444]",
    },
    warning: {
      bg: "bg-[#F59E0B]/10",
      border: "border-[#F59E0B]/30",
      icon: "🟠",
      titleColor: "text-[#F59E0B]",
    },
    info: {
      bg: "bg-[#3B82F6]/10",
      border: "border-[#3B82F6]/30",
      icon: "🔵",
      titleColor: "text-[#3B82F6]",
    },
  }[level];

  return (
    <div className={`rounded-xl ${config.bg} border ${config.border} p-5`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">{config.icon}</span>
        <h4 className={`font-semibold ${config.titleColor}`}>{title}</h4>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/60">
            <span className="text-white/30 mt-1">•</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Composant pour les formules/calculs
interface FormulaBlockProps {
  formula: string;
  description?: string;
}

export const FormulaBlock = ({ formula, description }: FormulaBlockProps) => {
  return (
    <div className="rounded-xl bg-[#1E293B]/50 border border-white/[0.05] p-6 font-mono text-center">
      <code className="text-lg sm:text-xl text-[#F59E0B]">{formula}</code>
      {description && (
        <p className="text-sm text-white/40 mt-3 font-sans">{description}</p>
      )}
    </div>
  );
};

// Composant pour les exemples concrets
interface ExampleBlockProps {
  title?: string;
  content: string;
  highlight?: string;
}

export const ExampleBlock = ({ title, content, highlight }: ExampleBlockProps) => {
  return (
    <div className="rounded-xl bg-gradient-to-br from-[#1A365D]/20 to-transparent border border-[#1A365D]/30 p-6">
      {title && (
        <p className="text-xs uppercase tracking-wider text-[#F59E0B] font-medium mb-3">
          {title}
        </p>
      )}
      <p className="text-white/70 leading-relaxed">
        {content}
      </p>
      {highlight && (
        <p className="text-lg font-semibold text-white mt-3">
          {highlight}
        </p>
      )}
    </div>
  );
};

export default FeatureSection;
